import React from 'react';

import {
  PaymentMethod,
  UserPaymentPlanOption
} from '../../../generated/graphql';
import { formatCAD } from '../../../utils/currencyFormat';
import PaymentOptions from '../PaymentOptions/PaymentOptions';
import PaymentPlanOptions from '../PaymentPlanOptions/PaymentPlanOptions';
import ProceedButton from '../ProceedButton/ProceedButton';
import ProductAccordion from '../ProductAccordion/ProductAccordion';

export type LobType = 'GL' | 'EO';

export type PlansByLob = Partial<Record<LobType, UserPaymentPlanOption[]>>;

const LOB_LABEL: Record<LobType, string> = {
  GL: 'General Liability',
  EO: 'Errors & Omissions'
};

interface PaymentScreenProps {
  plansByLob: PlansByLob;
  planOptions: UserPaymentPlanOption[];
  isInitial: boolean;
  paymentMethod: PaymentMethod;
  setPaymentMethod: (method: PaymentMethod) => void;
  paymentPlanOption: UserPaymentPlanOption | undefined;
  setPaymentPlanOption: (option: UserPaymentPlanOption) => void;
  useCardForRenewals: boolean;
  setUseCardForRenewals: (value: boolean) => void;
  country: string;
  disableProceedButtonOnceClick: boolean;
  onProceed: () => void;
}

const PaymentScreen: React.FC<PaymentScreenProps> = ({
  plansByLob,
  planOptions,
  isInitial,
  paymentMethod,
  setPaymentMethod,
  paymentPlanOption,
  setPaymentPlanOption,
  useCardForRenewals,
  setUseCardForRenewals,
  country,
  disableProceedButtonOnceClick,
  onProceed
}) => {
  // Iterate the LOBs the caller actually gave us, in insertion order. No
  // static GL/EO enumeration in the iteration logic — the universe of LOBs
  // only shows up via LOB_LABEL.
  const presentLobs = (Object.entries(plansByLob) as [
    LobType,
    UserPaymentPlanOption[] | undefined
  ][]).flatMap(([lob, plans]) => (plans ? [{ lob, plans }] : []));
  // Per-LOB plan selected by the shared paymentPlanId, plus the combined total.
  const productPlans = presentLobs.map(({ lob, plans }) => ({
    lob,
    plan: plans.find(
      (p) => p.paymentPlanId === paymentPlanOption?.paymentPlanId
    )
  }));
  const total = productPlans.reduce(
    (sum, { plan }) => sum + (plan?.totalPayable ?? 0),
    0
  );
  const isMultiProduct = presentLobs.length > 1;
  return (
    <div
      className="InitiatePayment m-auto text-lg sm:text-xl"
      data-testid="InitiatePayment"
    >
      <div className="container">
        <div className="uppercase text-left p-2 text-2xl bg-secondary rounded-md border-none font-bold">
          Complete Payment
        </div>
        <div className="text-xl my-4">
          Please choose a payment option and complete your payment
        </div>
        <div className="col-span-1 uppercase text-left p-2 text-lg bg-gray-400 rounded-md border-none font-bold mb-4">
          Billing Information
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 justify-start gap-x-10 gap-y-6">
          <div className="sm:row-start-2 sm:col-span-auto">
            {paymentPlanOption ? (
              <>
                <div className="text-secondary font-bold text-xl">
                  TOTAL:{' '}
                  <span className="text-black">{formatCAD.format(total)}</span>
                </div>
                <hr className="my-2" />
                {isMultiProduct ? (
                  <div className="border border-tertiary bg-white text-lg rounded-md p-3 my-3 max-w">
                    <span className="text-tertiary font-extrabold">Note:</span>{' '}
                    Your General Liability and Errors &amp; Omissions coverages
                    will be issued as separate policies, with their own invoices
                    charged separately.
                  </div>
                ) : null}
              </>
            ) : null}
            {productPlans.map(({ lob, plan }) =>
              plan ? (
                <ProductAccordion
                  key={lob}
                  label={LOB_LABEL[lob]}
                  plan={plan}
                  paymentMethod={paymentMethod}
                  isInitial={isInitial}
                  country={country}
                />
              ) : null
            )}
          </div>
          <div className="sm:row-start-2 sm:col-span-auto">
            <PaymentOptions
              paymentMethod={paymentMethod}
              setPaymentMethod={setPaymentMethod}
              useCardForRenewals={useCardForRenewals}
              setUseCardForRenewals={setUseCardForRenewals}
            />
          </div>
          <div className="sm:row-start-2 sm:col-span-auto">
            <PaymentPlanOptions
              userPlanData={planOptions}
              paymentPlanOption={paymentPlanOption}
              setPaymentPlanOption={setPaymentPlanOption}
              showCombinedNote={isMultiProduct}
            />
          </div>
        </div>
        {
          /* istanbul ignore next -- paymentMethod always has a default value */ paymentMethod !==
          undefined ? (
            <div className="text-center my-4">
              {disableProceedButtonOnceClick ? null : ( // TODO: Prefer disabling the button over hiding it — hiding might cause confusion.
                <ProceedButton
                  paymentMethod={paymentMethod}
                  onClick={onProceed}
                />
              )}
            </div>
          ) : null
        }
      </div>
    </div>
  );
};

export default PaymentScreen;
